import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Group,
  Stack,
  Badge,
  Avatar,
  ThemeIcon,
  Modal,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  SimpleGrid,
  Box,
  Collapse,
  ActionIcon,
  Loader,
  Center,
  ScrollArea,
  Divider,
  Paper,
  Switch
} from '@mantine/core';
import { personasAPI, preferencesAPI } from '../services/api';
import { useSubscription } from '../contexts/SubscriptionContext';
import UsageIndicator from '../components/UsageIndicator';
import ProBadge from '../components/ProBadge';

const PersonasPage = () => {
  const navigate = useNavigate();
  const { handleLimitError, refreshUsageStats, isProUser } = useSubscription();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedPersonaId, setExpandedPersonaId] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [personaPreferences, setPersonaPreferences] = useState({});
  const [showPrefsForm, setShowPrefsForm] = useState(false);
  const [currentPersonaId, setCurrentPersonaId] = useState(null);
  const [prefForm, setPrefForm] = useState({
    category: 'Technology',
    tag: '',
    weight: 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [personaToDelete, setPersonaToDelete] = useState(null);

  const categories = [
    'Technology', 'Food', 'Music', 'Sports', 'Books',
    'Movies', 'Games', 'Fashion', 'Travel', 'Pets',
    'Art', 'Cooking', 'Fitness', 'Photography'
  ];

  const [formData, setFormData] = useState({
    name: '',
    age: '',
    notes: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    relationship: '',
    favorite_holiday: '',
    is_shared: false // Default to private
  });

  useEffect(() => {
    loadPersonas();

    if (window.location.hash === '#create') {
      setShowCreateForm(true);
      window.history.replaceState(null, null, window.location.pathname);
    }
  }, []);

  const loadPersonas = async () => {
    try {
      const response = await personasAPI.getAll();
      setPersonas(response.data);
    } catch (error) {
      console.error('Failed to load personas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleCreatePersona = async () => {
    if (!formData.name.trim()) {
      alert('Persona name is required');
      return;
    }

    try {
      await personasAPI.create(formData);
      setFormData({ name: '', age: '', notes: '', email: '', phone: '', city: '', state: '', relationship: '', favorite_holiday: '' });
      setShowCreateForm(false);
      await loadPersonas();
      await refreshUsageStats(); // Refresh usage after creating
    } catch (error) {
      // Check if it's a limit error
      if (!handleLimitError(error)) {
        alert('Failed to create persona: ' + (error.response?.data?.error || 'Unknown error'));
      }
    }
  };

  const loadPersonaPreferences = async (personaId, forceReload = false) => {
    if (personaPreferences[personaId] && !forceReload) return;

    try {
      setPrefsLoading(true);
      const response = await preferencesAPI.getForSubject('persona', personaId);
      setPersonaPreferences(prev => ({
        ...prev,
        [personaId]: response.data
      }));
    } catch (error) {
      console.error('Failed to load persona preferences:', error);
      setPersonaPreferences(prev => ({
        ...prev,
        [personaId]: []
      }));
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleAddPreference = async () => {
    if (!prefForm.tag.trim()) {
      alert('Please enter a preference or interest');
      return;
    }

    const personaIdToUpdate = currentPersonaId;

    try {
      await preferencesAPI.create({
        subject_type: 'persona',
        subject_id: personaIdToUpdate,
        category: prefForm.category,
        tag: prefForm.tag,
        weight: prefForm.weight
      });

      setPrefForm({
        category: 'Technology',
        tag: '',
        weight: 0
      });
      setShowPrefsForm(false);
      setCurrentPersonaId(null);

      await loadPersonaPreferences(personaIdToUpdate, true);
    } catch (error) {
      alert('Failed to add preference: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeletePreference = async (prefId, personaId) => {
    try {
      await preferencesAPI.delete(prefId);
      await loadPersonaPreferences(personaId, true);
    } catch (error) {
      alert('Failed to delete preference');
    }
  };

  const togglePersonaExpansion = async (personaId) => {
    if (expandedPersonaId === personaId) {
      setExpandedPersonaId(null);
    } else {
      setExpandedPersonaId(personaId);
      if (!personaPreferences[personaId]) {
        await loadPersonaPreferences(personaId);
      }
    }
  };

  const getWeightColor = (weight) => {
    if (weight >= 3) return 'teal';
    if (weight >= 1) return 'green';
    if (weight <= -3) return 'red';
    if (weight <= -1) return 'orange';
    return 'gray';
  };

  const getWeightText = (weight) => {
    if (weight > 0) return `+${weight}`;
    return weight === 0 ? '±0' : weight.toString();
  };

  const handleDeletePersona = async () => {
    if (!personaToDelete) return;

    try {
      await personasAPI.delete(personaToDelete.id);
      setShowDeleteModal(false);
      setPersonaToDelete(null);
      await loadPersonas(); // Refresh the list
      await refreshUsageStats(); // Refresh usage after deleting
    } catch (error) {
      // Handle specific error cases with better UX
      if (error.response?.status === 403 && error.response?.data?.feature_type === 'delete_temporary_persona') {
        // Show upgrade prompt for temporary persona deletion on free tier
        alert(error.response.data.message + '\n\nUpgrade to Pro for full control over all your personas.');
        // Navigate to upgrade/subscription page
        navigate('/account?tab=subscription');
      } else {
        alert('Failed to delete persona: ' + (error.response?.data?.error || 'Unknown error'));
      }
    }
  };

  if (loading) {
    return (
      <Center h="70vh">
        <Stack align="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text c="dimmed" size="lg">Loading personas...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Box style={{ background: 'linear-gradient(180deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)' }}>
      <Container size="xl" py="xl" px="md">
        {/* Header */}
        <Box mb="xl" py="lg">
          <Group justify="space-between" align="flex-start">
            <Box>
              <Group align="center" gap="sm" mb="xs">
                <Title 
                  order={1} 
                  size="h1"
                  fw={700}
                  style={{
                    background: 'linear-gradient(45deg, #1971c2 0%, #15aabf 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  🎭 Your Personas
                </Title>
                {isProUser && <ProBadge size="lg" />}
              </Group>
              <Text size="xl" c="dimmed" fw={400}>
                Manage profiles for personalized gift recommendations
              </Text>
            </Box>
            <Button 
              size="lg" 
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
              onClick={() => setShowCreateForm(true)}
              style={{ boxShadow: '0 4px 12px rgba(25,113,194,0.3)' }}
            >
              ✨ New Persona
            </Button>
          </Group>
        </Box>

        {/* Usage Indicator - only for free users */}
        {!isProUser && (
          <Box mb="lg">
            <UsageIndicator feature="personas" title="Personas" />
          </Box>
        )}

        {/* Personas Grid */}
        {personas.length === 0 ? (
          <Card
            p="xl"
            radius="lg"
            withBorder
            style={{
              background: 'linear-gradient(135deg, rgba(25,113,194,0.05) 0%, rgba(0,0,0,0) 100%)',
              borderStyle: 'dashed',
              borderWidth: '2px'
            }}
          >
            <Center py="xl">
              <Stack align="center" gap="md">
                <ThemeIcon size={100} radius="xl" variant="light" color="blue">
                  <Text size="60px">🎭</Text>
                </ThemeIcon>
                <Title order={2} ta="center">
                  No personas yet
                </Title>
                <Text c="dimmed" ta="center" maw={500}>
                  Create your first persona profile to unlock personalized AI-powered gift recommendations
                </Text>
                <Button 
                  size="lg" 
                  variant="gradient"
                  gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
                  onClick={() => setShowCreateForm(true)}
                  mt="md"
                >
                  Create First Persona
                </Button>
              </Stack>
            </Center>
          </Card>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {personas.map((persona) => (
              <Card
                key={persona.id}
                p="lg"
                radius="lg"
                withBorder
                shadow="sm"
                style={{
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <Group justify="space-between" mb="md">
                  <Group gap="sm">
                    <Text
                      fw={700}
                      size="xl"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/products?persona=${persona.id}`)}
                    >
                      {persona.name}
                    </Text>
                    {persona.is_temporary && (
                      <Badge size="sm" color="orange" variant="light">
                        ⏰ Temporary
                      </Badge>
                    )}
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    size="lg"
                    onClick={() => togglePersonaExpansion(persona.id)}
                  >
                    {expandedPersonaId === persona.id ? '−' : '+'}
                  </ActionIcon>
                </Group>

                <Stack gap="xs" mb="md">
                  {persona.age && (
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="xl" variant="light" color="blue">
                        🎂
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">Age {persona.age}</Text>
                    </Group>
                  )}
                  {persona.relationship && (
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="xl" variant="light" color="pink">
                        💝
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">{persona.relationship}</Text>
                    </Group>
                  )}
                  {(persona.city && persona.state) && (
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="xl" variant="light" color="teal">
                        📍
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">{persona.city}, {persona.state}</Text>
                    </Group>
                  )}
                  {persona.favorite_holiday && (
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="xl" variant="light" color="orange">
                        🎉
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">
                        {persona.favorite_holiday.charAt(0).toUpperCase() + persona.favorite_holiday.slice(1).replace('-', ' ')}
                      </Text>
                    </Group>
                  )}
                </Stack>

                {persona.notes && (
                  <Text size="sm" c="dimmed" mb="md" lineClamp={3}>
                    {persona.notes}
                  </Text>
                )}

                <Stack gap="xs" mb="md">
                  <Button
                    variant="light"
                    color="blue"
                    fullWidth
                    onClick={() => navigate(`/products?persona=${persona.id}`)}
                  >
                    🎁 Find Gifts
                  </Button>
                  <Group grow>
                    <Button
                      variant="subtle"
                      color="gray"
                      onClick={() => navigate(`/personas/new?edit=${persona.id}`)}
                    >
                      ✏️ Edit
                    </Button>
                    {/* Hide delete button for temporary personas on free tier */}
                    {!(persona.is_temporary && !isProUser) && (
                      <Button
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          setPersonaToDelete(persona);
                          setShowDeleteModal(true);
                        }}
                      >
                        🗑️ Delete
                      </Button>
                    )}
                  </Group>
                </Stack>

                {/* Sharing Toggle - only show for non-temporary personas */}
                {!persona.is_temporary && (
                  <Group justify="space-between" align="center" mb="md">
                    <Text size="sm" fw={500}>
                      👥 Share with friends
                    </Text>
                    <Switch
                      size="md"
                      color="teal"
                      checked={persona.is_shared || false}
                      onChange={async (event) => {
                        try {
                          await personasAPI.toggleSharing(persona.id, event.currentTarget.checked);
                          await loadPersonas(); // Refresh to show updated sharing status
                        } catch (error) {
                          alert('Failed to update sharing status: ' + (error.response?.data?.error || 'Unknown error'));
                        }
                      }}
                    />
                  </Group>
                )}

                <Collapse in={expandedPersonaId === persona.id}>
                  <Divider my="md" />
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text fw={600} size="sm">
                        Preferences & Interests
                      </Text>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          setCurrentPersonaId(persona.id);
                          setShowPrefsForm(!showPrefsForm);
                        }}
                      >
                        {showPrefsForm && currentPersonaId === persona.id ? 'Cancel' : '+ Add'}
                      </Button>
                    </Group>

                    {showPrefsForm && currentPersonaId === persona.id && (
                      <Paper p="sm" withBorder radius="md">
                        <Stack gap="xs">
                          <Select
                            label="Category"
                            data={categories}
                            value={prefForm.category}
                            onChange={(value) => setPrefForm({ ...prefForm, category: value })}
                            size="xs"
                          />
                          <TextInput
                            label="Interest/Tag"
                            placeholder="e.g., coffee, gaming"
                            value={prefForm.tag}
                            onChange={(e) => setPrefForm({ ...prefForm, tag: e.target.value })}
                            size="xs"
                          />
                          <Select
                            label="Weight"
                            data={Array.from({length: 11}, (_, i) => {
                              const val = i - 5;
                              return { value: val.toString(), label: val > 0 ? `+${val}` : val.toString() };
                            })}
                            value={prefForm.weight.toString()}
                            onChange={(value) => setPrefForm({ ...prefForm, weight: parseInt(value) })}
                            size="xs"
                          />
                          <Button size="xs" onClick={handleAddPreference}>
                            Add Preference
                          </Button>
                        </Stack>
                      </Paper>
                    )}

                    {prefsLoading ? (
                      <Center py="sm">
                        <Loader size="sm" />
                      </Center>
                    ) : personaPreferences[persona.id] && personaPreferences[persona.id].length > 0 ? (
                      <ScrollArea h={200}>
                        <Stack gap="xs">
                          {personaPreferences[persona.id].map(pref => (
                            <Paper key={pref.id} p="xs" withBorder radius="md">
                              <Group justify="space-between">
                                <Group gap="xs">
                                  <Badge size="xs" variant="light">
                                    {pref.category}
                                  </Badge>
                                  <Text size="sm">{pref.tag}</Text>
                                </Group>
                                <Group gap="xs">
                                  <Badge
                                    size="sm"
                                    color={getWeightColor(pref.weight)}
                                    variant="filled"
                                  >
                                    {getWeightText(pref.weight)}
                                  </Badge>
                                  <ActionIcon
                                    size="sm"
                                    color="red"
                                    variant="subtle"
                                    onClick={() => handleDeletePreference(pref.id, persona.id)}
                                  >
                                    ✕
                                  </ActionIcon>
                                </Group>
                              </Group>
                            </Paper>
                          ))}
                        </Stack>
                      </ScrollArea>
                    ) : (
                      <Text size="sm" c="dimmed" ta="center" py="sm">
                        No preferences yet
                      </Text>
                    )}
                  </Stack>
                </Collapse>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Container>

      {/* Create Modal */}
      <Modal
        opened={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title={
          <Text size="xl" fw={700}>
            ✨ Create New Persona
          </Text>
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g., Tech-Savvy Sarah"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            size="md"
          />

          <NumberInput
            label="Age (optional)"
            placeholder="e.g., 25"
            value={formData.age}
            onChange={(value) => handleInputChange('age', value)}
            size="md"
            min={0}
            max={120}
          />

          <Divider label="📞 Contact Information" labelPosition="center" />

          <Group grow>
            <TextInput
              label="Email"
              placeholder="email@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              size="md"
            />
            <TextInput
              label="Phone"
              placeholder="(555) 123-4567"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              size="md"
            />
          </Group>

          <Divider label="📍 Location" labelPosition="center" />

          <Group grow>
            <TextInput
              label="City"
              placeholder="San Francisco"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              size="md"
            />
            <TextInput
              label="State"
              placeholder="California"
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              size="md"
            />
          </Group>

          <Divider label="💝 Relationship & Celebrations" labelPosition="center" />

          <Group grow>
            <Select
              label="Relationship Type"
              placeholder="Choose relationship..."
              data={[
                { value: 'family', label: 'Family Member' },
                { value: 'friend', label: 'Close Friend' },
                { value: 'colleague', label: 'Work Colleague' },
                { value: 'partner', label: 'Romantic Partner' },
                { value: 'acquaintance', label: 'Acquaintance' }
              ]}
              value={formData.relationship}
              onChange={(value) => handleInputChange('relationship', value)}
              size="md"
            />
            <Select
              label="Favorite Holiday"
              placeholder="Choose holiday..."
              data={[
                { value: 'birthday', label: 'Birthday' },
                { value: 'christmas', label: 'Christmas' },
                { value: 'hanukkah', label: 'Hanukkah' },
                { value: 'thanksgiving', label: 'Thanksgiving' },
                { value: 'easter', label: 'Easter' },
                { value: 'mothers-day', label: "Mother's Day" },
                { value: 'fathers-day', label: "Father's Day" },
                { value: 'valentines', label: "Valentine's Day" },
                { value: 'halloween', label: 'Halloween' },
                { value: 'anniversary', label: 'Anniversary' },
                { value: 'graduation', label: 'Graduation' }
              ]}
              value={formData.favorite_holiday}
              onChange={(value) => handleInputChange('favorite_holiday', value)}
              size="md"
            />
          </Group>

          <Textarea
            label="Personality & Preferences"
            placeholder="Describe personality, hobbies, interests, style preferences, dietary needs, favorite colors, brands, etc."
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={4}
            size="md"
          />

          <Divider label="🔒 Privacy Settings" labelPosition="center" />

          <Group justify="space-between">
            <Box>
              <Text size="md" fw={500}>Share with friends</Text>
              <Text size="sm" c="dimmed">
                Allow friends to view this persona and suggest preferences
              </Text>
            </Box>
            <Switch
              size="lg"
              color="teal"
              checked={formData.is_shared}
              onChange={(event) => handleInputChange('is_shared', event.currentTarget.checked)}
            />
          </Group>

          <Group grow>
            <Button
              size="lg"
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
              onClick={handleCreatePersona}
            >
              Create Persona
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setShowCreateForm(false)}
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={
          <Text size="lg" fw={700} c="red">
            🗑️ Delete Persona
          </Text>
        }
        size="md"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete <strong>{personaToDelete?.name}</strong>?
          </Text>
          <Text size="sm" c="dimmed">
            This action cannot be undone. All preferences and associated data will be permanently removed.
          </Text>

          <Group grow>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeletePersona}
            >
              Delete Persona
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default PersonasPage;
